import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader, Calendar, Clock, Timer, CreditCard, Users, BookOpen, Search } from 'lucide-react';
import { theme } from '../theme';
import { apiUtils } from '../utils/apiUtils';

interface BookingData {
  id: string;
  venueId: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  totalAmount: number;
  durationHours?: number;
  status: string;
  venue: {
    id: string;
    name: string;
    address: string;
    venueType: string;
    capacity?: number;
    surfaceType?: string;
    isIndoor?: boolean;
    hasLighting?: boolean;
    city?: string;
    province?: string;
    photos?: string[];
    complex?: {
      name: string;
    };
  };
  match?: {
    id: string;
    code: string;
  };
}

interface PrefillData {
  title: string;
  date: string;
  time: string;
  endTime: string;
  location: string;
  googleMapsUrl: string;
  maxPlayers: number;
  venue: {
    id: string;
    name: string;
    type: string;
    address: string;
    complexName: string | null;
  };
  bookingId: string;
  totalAmount?: number;
}

interface CreatedMatch {
  id: string;
  code: string;
}

const venueTypeLabels: Record<string, string> = {
  futbol5: 'Fútbol 5',
  futbol7: 'Fútbol 7',
  futbol8: 'Fútbol 8',
  futbol9: 'Fútbol 9',
  futbol11: 'Fútbol 11',
};

const surfaceLabels: Record<string, string> = {
  sintetico: 'Césped Sintético',
  natural: 'Césped Natural',
  cemento: 'Cemento',
};

const BookingSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Leer parámetros de MercadoPago y booking_id o hold_id
  const holdId = searchParams.get('hold_id');
  const bookingId = searchParams.get('booking_id') || searchParams.get('external_reference');
  const mpStatus = searchParams.get('status'); // approved, pending, rejected

  // DEBUG: Ver todos los parámetros
  // eslint-disable-next-line no-console
  console.log('🔍 BookingSuccessPage - URL params:', {
    holdId,
    bookingId,
    mpStatus,
    allParams: Object.fromEntries(searchParams.entries())
  });

  const [status, setStatus] = useState<'checking' | 'approved' | 'pending' | 'rejected'>('checking');
  const [attempts, setAttempts] = useState(0);
  const [booking, setBooking] = useState<BookingData | null>(null);
  const [prefillData, setPrefillData] = useState<PrefillData | null>(null);
  const [creatingMatch, setCreatingMatch] = useState(false);
  const [createdMatch, setCreatedMatch] = useState<CreatedMatch | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('🔍 Verificando parámetros - holdId:', holdId, 'bookingId:', bookingId);

    if (!bookingId && !holdId) {
      // eslint-disable-next-line no-console
      console.log('❌ Sin bookingId ni holdId - redirigiendo a /venues');
      navigate('/venues');
      return;
    }

    // Si MercadoPago ya nos devolvió un status, usarlo como estado inicial
    // pero igual verificamos con el backend para confirmar
    const initialStatus = mpStatus === 'approved' ? 'checking' :
                          mpStatus === 'rejected' ? 'rejected' :
                          mpStatus === 'pending' ? 'pending' : 'checking';

    // Si MP dice rejected, mostrar inmediatamente sin polling
    if (mpStatus === 'rejected') {
      setStatus('rejected');
      return;
    }

    const fetchBooking = async () => {
      try {
        // Si tenemos hold_id, buscar booking desde hold
        const endpoint = holdId
          ? `/api/bookings/by-hold/${holdId}`
          : `/api/bookings/${bookingId}`;

        const response = await apiUtils.get(endpoint);

        // Si viene de hold, el booking estará en response.data.booking
        const bookingData = holdId ? response.data?.booking : response.data;

        if (bookingData) {
          setBooking(bookingData);
          if (bookingData.match) {
            setCreatedMatch(bookingData.match);
          }
          // Si el booking ya está CONFIRMED, marcar como approved
          if (bookingData.status === 'CONFIRMED') {
            setStatus('approved');
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching booking:', error);
      }
    };

    const fetchPrefill = async () => {
      // Solo buscar prefill si ya tenemos booking ID
      if (!bookingId && !booking?.id) return;

      try {
        const id = bookingId || booking?.id;
        const response = await apiUtils.get(`/api/bookings/${id}/match-prefill`);
        setPrefillData(response.data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching prefill:', error);
      }
    };

    fetchBooking();
    if (!holdId) {
      fetchPrefill();
    }

    // Función para iniciar polling
    const startPolling = () => {
      const pollInterval = setInterval(async () => {
        try {
          // Si tenemos hold_id, polling para obtener booking desde hold
          if (holdId) {
            const response = await apiUtils.get(`/api/bookings/by-hold/${holdId}`);
            if (response.data?.booking) {
              const bookingData = response.data.booking;
              setBooking(bookingData);
              if (bookingData.status === 'CONFIRMED') {
                setStatus('approved');
                clearInterval(pollInterval);
                // Ahora que tenemos el booking, fetch prefill usando el ID del booking
                try {
                  const prefillResponse = await apiUtils.get(`/api/bookings/${bookingData.id}/match-prefill`);
                  setPrefillData(prefillResponse.data);
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error('Error fetching prefill:', err);
                }
              }
            } else if (attempts >= 15) {
              // Timeout después de 30 segundos
              setStatus('pending');
              clearInterval(pollInterval);
            } else {
              setAttempts((prev) => prev + 1);
            }
          } else {
            // Legacy flow: tenemos booking_id directamente
            const response = await apiUtils.get(`/api/bookings/${bookingId}/payment/status`);
            if (response.data.status === 'approved') {
              setStatus('approved');
              clearInterval(pollInterval);
            } else if (attempts >= 15) {
              setStatus('pending');
              clearInterval(pollInterval);
            } else {
              setAttempts((prev) => prev + 1);
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error checking payment status:', error);
          setAttempts((prev) => prev + 1);
        }
      }, 2000);

      return () => clearInterval(pollInterval);
    };

    // Si MP ya confirmó approved (legacy flow), verificar directamente
    if (mpStatus === 'approved' && !holdId) {
      const verifyPayment = async () => {
        try {
          const response = await apiUtils.get(`/api/bookings/${bookingId}/payment/status`);
          if (response.data.status === 'approved') {
            setStatus('approved');
          } else {
            startPolling();
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error verifying payment:', error);
          startPolling();
        }
      };
      verifyPayment();
      return;
    }

    // Iniciar polling para todos los demás casos
    const cleanup = startPolling();
    return cleanup;
  }, [bookingId, holdId, navigate, mpStatus, booking, attempts]);

  const handleCreateMatch = useCallback(async () => {
    const currentBookingId = bookingId || booking?.id;
    if (!currentBookingId || !prefillData) return;
    try {
      setCreatingMatch(true);
      setCreateError(null);
      const response = await apiUtils.post(`/api/bookings/${currentBookingId}/create-match`, {
        maxPlayers: prefillData.maxPlayers,
        price: prefillData.totalAmount || 0,
        team1Name: 'Equipo 1',
        team2Name: 'Equipo 2',
        team1Color: '#4CAF50',
        team2Color: '#2196F3',
      });
      setCreatedMatch(response.data.match);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error creating match:', err);
      setCreateError(err.response?.data?.error || 'Error al crear partido');
    } finally {
      setCreatingMatch(false);
    }
  }, [bookingId, booking, prefillData]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-AR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).toUpperCase();
  };

  const formatShortDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-AR', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
    }).replace('.', '').toUpperCase();
  };

  const calculateDuration = (start: string, end: string) => {
    const [startH] = start.split(':').map(Number);
    const [endH] = end.split(':').map(Number);
    return endH - startH;
  };

  const generateBookingNumber = (id: string) => {
    const year = new Date().getFullYear();
    const num = id.slice(-5).toUpperCase();
    return `#RES-${year}-${num}`;
  };

  // --- STYLES ---
  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: '#181A1B',
    display: 'flex',
    flexDirection: 'column',
  };


  const successHeaderStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
    padding: 48,
    backgroundColor: '#23272A',
  };

  const successIconStyle: React.CSSProperties = {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#00E676',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const mainContentStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px 48px',
  };

  const contentColumnStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
    maxWidth: 1200,
    width: '100%',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: '#23272A',
    borderRadius: 16,
    padding: 32,
    border: '2px solid #00E676',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontSize: 24,
    fontWeight: 'normal',
    color: '#FFFFFF',
    margin: 0,
  };

  const smallTitleStyle: React.CSSProperties = {
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontSize: 18,
    fontWeight: 'normal',
    color: '#FFFFFF',
    margin: 0,
  };

  const bookingNumberBoxStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: '#181A1B',
    borderRadius: 10,
  };

  const venueRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: 20,
    alignItems: 'flex-start',
  };

  const venueImageStyle: React.CSSProperties = {
    width: 80,
    height: 80,
    borderRadius: 12,
    objectFit: 'cover',
    backgroundColor: '#33383B',
  };

  const dateTimeGridStyle: React.CSSProperties = {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
  };

  const infoBoxStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 150,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: '#181A1B',
    borderRadius: 12,
    border: '1px solid #33383B',
  };

  const dividerStyle: React.CSSProperties = {
    height: 1,
    backgroundColor: '#33383B',
    width: '100%',
  };

  const paymentRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const paymentMethodStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    backgroundColor: '#181A1B',
    borderRadius: 10,
  };

  const statusBadgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 24px',
    backgroundColor: '#00E676',
    borderRadius: 10,
    color: '#181A1B',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontSize: 16,
    fontWeight: 'normal',
  };

  const twoColumnLayout: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 32,
    marginTop: 0,
    minHeight: '500px',
  };

  const detailCard: React.CSSProperties = {
    background: '#1E1E1E',
    border: '2px solid #333',
    borderRadius: 16,
    padding: 40,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  };

  const createCard: React.CSSProperties = {
    background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
    border: '4px solid #00E676',
    borderRadius: 16,
    padding: 48,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
    alignItems: 'center',
    justifyContent: 'center',
  };

  const actionButtonStyle = (isPrimary: boolean, large: boolean = false): React.CSSProperties => ({
    width: '100%',
    padding: large ? '16px 32px' : '12px 20px',
    backgroundColor: isPrimary ? '#00E676' : 'transparent',
    color: isPrimary ? '#181A1B' : '#B0BEC5',
    border: isPrimary ? 'none' : '1px solid #33383B',
    borderRadius: 8,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontSize: large ? 18 : 14,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontWeight: large ? 600 : 400,
  });

  // --- CHECKING STATE ---
  if (status === 'checking') {
    return (
      <div style={{ ...pageStyle, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ ...successIconStyle, backgroundColor: 'rgba(0, 230, 118, 0.1)', marginBottom: 20 }}>
            <Loader size={40} color="#00E676" className="spinner" />
          </div>
          <h1 style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 32, color: '#FFFFFF', marginBottom: 8 }}>
            Verificando Pago...
          </h1>
          <p style={{ color: '#B0BEC5', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 16 }}>
            Por favor aguardá unos segundos
          </p>
        </div>
        <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinner {
          animation: spin 1s linear infinite;
        }

        @media (max-width: 768px) {
          .two-column-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div style={{ ...pageStyle, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ ...successIconStyle, backgroundColor: 'rgba(255, 193, 7, 0.1)', marginBottom: 20 }}>
            <Loader size={40} color="#FFC107" />
          </div>
          <h1 style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 32, color: '#FFFFFF', marginBottom: 8 }}>
            Pago Pendiente
          </h1>
          <p style={{ color: '#B0BEC5', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 16, marginBottom: 24 }}>
            Tu pago está siendo procesado. Te notificaremos cuando esté confirmado.
          </p>
          <button
            style={{ ...actionButtonStyle(true), width: 'auto', padding: '16px 32px' }}
            onClick={() => navigate('/my-bookings')}
          >
            Ver Mis Reservas
          </button>
        </div>
      </div>
    );
  }

  // --- REJECTED STATE ---
  if (status === 'rejected') {
    return (
      <div style={{ ...pageStyle, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 450, padding: '0 20px' }}>
          <div style={{ ...successIconStyle, backgroundColor: 'rgba(244, 67, 54, 0.1)', marginBottom: 20 }}>
            <span style={{ fontSize: 40, color: '#F44336' }}>✕</span>
          </div>
          <h1 style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 32, color: '#F44336', marginBottom: 8 }}>
            Pago Rechazado
          </h1>
          <p style={{ color: '#B0BEC5', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 16, marginBottom: 24 }}>
            Tu pago no pudo ser procesado. Por favor, intentá nuevamente con otro medio de pago.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button
              style={{ ...actionButtonStyle(true), width: '100%', padding: '16px 32px' }}
              onClick={() => bookingId && navigate(`/payment/${bookingId}`)}
            >
              Reintentar Pago
            </button>
            <button
              style={{ ...actionButtonStyle(false), width: '100%', padding: '16px 32px' }}
              onClick={() => navigate('/venues')}
            >
              Volver a Canchas
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- APPROVED ---
  const venue = booking?.venue;
  const venuePhoto = venue?.photos?.[0];
  const venueTypeLabel = venue?.venueType ? venueTypeLabels[venue.venueType] || venue.venueType : '';
  const surfaceLabel = venue?.surfaceType ? surfaceLabels[venue.surfaceType] || venue.surfaceType : '';
  const duration = booking ? calculateDuration(booking.startTime, booking.endTime) : 1;

  return (
    <div style={pageStyle}>
      {/* Success Header */}
      <div style={successHeaderStyle}>
        <div style={successIconStyle}>
          <Check size={48} color="#181A1B" strokeWidth={3} />
        </div>
        <h1 style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 42, color: '#00E676', margin: 0 }}>
          ¡RESERVA CONFIRMADA!
        </h1>
        <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 16, color: '#B0BEC5', textAlign: 'center', margin: 0 }}>
          Tu reserva ha sido procesada exitosamente. Recibirás un email de confirmación.
        </p>
      </div>

      {/* Main Content */}
      <div style={mainContentStyle}>
        <div style={contentColumnStyle}>
          {/* Two Column Layout: Detalle Reserva + Crear Partido */}
          <div className="two-column-layout" style={twoColumnLayout}>
            {/* Left Column: Detalle Reserva */}
            <div style={detailCard}>
              <h3 style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 28, color: '#FFFFFF', margin: 0, marginBottom: 8 }}>
                📋 DETALLE RESERVA
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: '#B0BEC5' }}>
                    Cancha:
                  </span>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 16, color: '#FFFFFF', fontWeight: 600 }}>
                    {venue?.name}
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: '#B0BEC5' }}>
                    Fecha:
                  </span>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 16, color: '#FFFFFF', fontWeight: 600 }}>
                    {formatShortDate(booking?.bookingDate || '')}
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: '#B0BEC5' }}>
                    Hora:
                  </span>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 16, color: '#FFFFFF', fontWeight: 600 }}>
                    {booking?.startTime} - {booking?.endTime}
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: '#B0BEC5' }}>
                    Precio:
                  </span>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 24, color: '#00E676', fontWeight: 600 }}>
                    ${booking?.totalAmount.toLocaleString('es-AR')}
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: '#B0BEC5' }}>
                    Estado:
                  </span>
                  <span style={{
                    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                    fontSize: 18,
                    color: '#00E676',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}>
                    ✅ CONFIRMADA
                  </span>
                </div>
              </div>

              <button
                style={{ ...actionButtonStyle(false, false), marginTop: 'auto', fontSize: 16, padding: '14px 24px' }}
                onClick={() => navigate('/my-bookings')}
              >
                MIS RESERVAS
              </button>
            </div>

            {/* Right Column: Crear Partido */}
            <div style={createCard}>
              <h3 style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 32, color: '#FFFFFF', margin: 0 }}>
                ⚽ CREAR PARTIDO
              </h3>

              <p style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 18,
                color: '#E0E0E0',
                margin: 0,
                lineHeight: 1.6,
                fontWeight: 500
              }}>
                Tu cancha está lista. Ahora creá el partido en Futbolero!
              </p>

              <button
                style={{
                  ...actionButtonStyle(true, true),
                  fontSize: 22,
                  padding: '20px 48px',
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(0, 230, 118, 0.3)'
                }}
                disabled={creatingMatch}
                onClick={() => {
                  if (createdMatch) {
                    navigate(`/matches/${createdMatch.id}`);
                  } else if (!creatingMatch) {
                    handleCreateMatch();
                  }
                }}
              >
                {creatingMatch ? 'CREANDO...' : createdMatch ? 'IR AL PARTIDO →' : 'CREAR PARTIDO →'}
              </button>

              {createError && (
                <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: '#ff6b6b', fontWeight: 600 }}>
                  {createError}
                </span>
              )}

              {createdMatch && (
                <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: '#E0E0E0', fontWeight: 600 }}>
                  Código: {createdMatch.code}
                </span>
              )}

              <button
                style={{
                  ...actionButtonStyle(false, false),
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: '#E0E0E0',
                  fontSize: 16,
                  padding: '14px 32px'
                }}
                onClick={() => navigate('/venues')}
              >
                BUSCAR CANCHAS
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinner {
          animation: spin 1s linear infinite;
        }

        @media (max-width: 768px) {
          .two-column-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BookingSuccessPage;
